import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Object as ObjectEntity } from '@/types/entities'

interface ObjectDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    object: ObjectEntity | null
    onConfirm: () => void
    isLoading?: boolean
}

export function ObjectDeleteDialog({
    open,
    onOpenChange,
    object,
    onConfirm,
    isLoading = false,
}: ObjectDeleteDialogProps) {
    const { t } = useTranslation()

    if (!object) return null

    // Check if object can be deleted
    const canDelete = (object.noObjectVersions || 0) === 0 && 
                     (object.noSourceID || 0) === 0 && 
                     (object.noTargetID || 0) === 0

    const hasObjectVersions = (object.noObjectVersions || 0) > 0
    const hasMappings = (object.noSourceID || 0) > 0 || (object.noTargetID || 0) > 0

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        {t('objects.deleteObject')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('objects.deleteConfirm', { objectID: object.objectID || '' })}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {!canDelete && (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {hasObjectVersions && (
                                    <div>
                                        {t('objects.cannotDeleteObjectVersions', { count: object.noObjectVersions || 0 })}
                                    </div>
                                )}
                                {hasMappings && (
                                    <div>
                                        {t('objects.cannotDeleteMappings', { 
                                            sourceCount: object.noSourceID || 0,
                                            targetCount: object.noTargetID || 0
                                        })}
                                    </div>
                                )}
                            </AlertDescription>
                        </Alert>
                    )}

                    {canDelete && (
                        <Alert>
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {t('objects.deleteWarning')}
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div>
                                <strong>{t('objects.objectID')}:</strong> {object.objectID || '-'}
                            </div>
                            {object.objectDescription && (
                                <div>
                                    <strong>{t('objects.description')}:</strong> {object.objectDescription}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading || !canDelete}
                    >
                        {isLoading ? t('objects.deleting') : t('objects.deleteObject')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

